import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sparkles } from 'lucide-react';

const Header = ({ onCTAClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#como-funciona', label: 'Cómo Funciona' },
    { href: '#beneficios', label: 'Beneficios' },
    { href: '#precios', label: 'Precios' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'glass shadow-sm' 
            : 'bg-transparent'
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.a
              href="#"
              className="flex items-center gap-2 group"
              whileHover={{ scale: 1.02 }}
              data-testid="logo"
            >
              <div className="w-10 h-10 rounded-xl bg-[#2E5C41] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-cabinet text-xl md:text-2xl font-bold text-[#1A2E24] tracking-tight">
                Habit<span className="text-[#2E5C41]">IA</span>
              </span>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[#4B5E53] hover:text-[#2E5C41] text-sm font-medium transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <motion.a
                href="https://app.habitai.fit"
                className="px-6 py-2.5 text-[#2E5C41] bg-transparent border-2 border-[#2E5C41] text-sm font-bold rounded-full hover:bg-[#2E5C41]/10 transition-all duration-300"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Login
              </motion.a>
              <motion.button
                onClick={onCTAClick}
                className="px-6 py-2.5 bg-[#2E5C41] text-white text-sm font-bold rounded-full hover:bg-[#1A2E24] transition-all duration-300"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                data-testid="cta-header"
              >
                Acceso Fundadores
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[#1A2E24]"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-[#FAFAF9] pt-20 px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-4">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-[#1A2E24] text-lg font-medium py-3 border-b border-[#2E5C41]/10"
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="https://app.habitai.fit"
                onClick={() => setIsMobileMenuOpen(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="mt-4 w-full py-4 text-[#2E5C41] bg-transparent border-2 border-[#2E5C41] text-center text-base font-bold rounded-full"
              >
                Login
              </motion.a>
              <motion.button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onCTAClick();
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-2 w-full py-4 bg-[#2E5C41] text-white text-base font-bold rounded-full"
                data-testid="cta-mobile-menu"
              >
                Acceso Fundadores
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
